import { axiosClient } from "~/js/network/axiosClient";
import type { TextOrFileMessage } from "../helpers/types";
import { ApiErrorHandlers, BaseApi } from "./baseApi";

export class ChatGptApi extends BaseApi {
	static chatGptUrl = "chat_gpt/";

	static async getChatGptImageVision(
		prompt: string,
		fileId: number,
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{ message: string }>(
				`${ChatGptApi.baseUrlV2}/tasks/gpt-vision`,
				{
					prompt,
					fileId
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async getChatGpt4o(
		prompt: string,
		fileId?: number,
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{ message: string }>(
				"api/v2/tasks/gpt-4o",
				{
					prompt,
					fileId
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}
	static async getChatGptDescribe(
		prompt: string,
		fileId: number,
		signal?: AbortController["signal"]
	) {
		try {
			const resp = await axiosClient.axiosClient.post<{ message: string }>(
				"api/v2/tasks/gpt-4o-describe",
				{
					prompt,
					fileId
				},
				{ signal: signal }
			);
			return resp.data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async getChatGpt4oPrompt(
		prompt: string,
		_fileId: number,
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{ message: string }>(
				"api/v2/tasks/gpt-prompt",
				{
					prompt
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async getChatGpt4oPromptWithoutTask(
		prompt: string,
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{ message: string }>(
				"api/v2/tasks/gpt-prompt-assist",
				{
					prompt
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async getPromptExtend(
		prompt: string,
		fileId: number,
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{ message: string }>(
				"api/v2/tasks/gpt-prompt-extend",
				{
					prompt,
					fileId
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async getChatGpt4oDialogue(
		messages: Omit<TextOrFileMessage, "fileSrc" | "fileName">[],
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{
				message: string;
				role: string;
			}>(
				"api/v2/tasks/gpt-vision-dialogue",
				{
					messages
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async getChatGptAssistaintDialogue(
		messages: Omit<TextOrFileMessage, "fileSrc" | "fileName">[],
		abortControler?: AbortController["signal"]
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<{
				message: string;
				role: string;
			}>(
				"api/v2/tasks/azure_ai_search",
				{
					messages
				},
				{ signal: abortControler }
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	/**
	 * Translate text to English using GPT-4o
	 * @param text - Text to translate
	 * @param abortControler - Optional abort controller signal
	 * @returns Translated text in English
	 */
	static async translateToEnglish(
		text: string,
		abortControler?: AbortController["signal"]
	): Promise<string> {
		try {
			const translationPrompt = `Translate the following text to English. Return ONLY the translation, without any additional text, explanations, or formatting:\n\n${text}`;

			const { data } = await axiosClient.axiosClient.post<{ message: string }>(
				"api/v2/tasks/gpt-prompt-assist",
				{
					prompt: translationPrompt
				},
				{ signal: abortControler }
			);
			return data.message.trim();
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}
}
